import { match } from 'ts-pattern'
import { FitpetMallEventEnum } from '@/constants/fitpet-mall-event.enum'
import { LocationEnum } from '@/constants/location.enum'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import { DisplayCollectionCollectionType } from '@/graphql/generated/schemas'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'

interface TrackClickHomeSectionListProductProps {
  product: ProductTypeDefinedByFrontend
  location?: LocationEnum
}

interface TrackClickHomeDisplayProductListProps extends TrackClickHomeSectionListProductProps {
  displayItemId?: string
  displayItemName?: string
}

interface TrackClickHomeSectionProductV4Props extends TrackClickHomeSectionListProductProps {
  sectionIndex?: number
  sectionName: string
}

interface TrackByDisplayCollectionTypeProps extends TrackClickHomeDisplayProductListProps {
  displayCollectionType: DisplayCollectionCollectionType
  title: string
  displayCollectionId: string
}

export const useHomeMoreListTracker = () => {
  const tracker = useTracker()

  const trackClickHomeNewProductEventV4 = ({ product, location }: TrackClickHomeSectionListProductProps) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeNewProduct, {
      product,
      isShownPriceCouponDiscounted: product?.hasApplicableProductCoupon,
      productPurchaseCouponDiscountPrice: product?.salePrice,
      location,
    })
  }
  const trackClickHomeMagazineBackgroundImageProductEvent = ({
    product,
    location,
  }: TrackClickHomeSectionListProductProps) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeMagazineBackgroundImageProduct, {
      product,
      isShownPriceCouponDiscounted: product?.hasApplicableProductCoupon,
      productPurchaseCouponDiscountPrice: product?.salePrice,
      location,
    })
  }

  const trackClickMagazineImageProductEvent = ({ product, location }: TrackClickHomeSectionListProductProps) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeMagazineImageProduct, {
      product,
      isShownPriceCouponDiscounted: product?.hasApplicableProductCoupon,
      productPurchaseCouponDiscountPrice: product?.salePrice,
      location,
    })
  }

  const trackClickHomeSpecialSaleProductEventV4 = ({ product, location }: TrackClickHomeSectionListProductProps) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeSpecialsaleProduct, {
      product,
      isShownPriceCouponDiscounted: product?.hasApplicableProductCoupon,
      productPurchaseCouponDiscountPrice: product?.salePrice,
      location,
    })
  }

  const trackClickHomeDisplayProductEventV4 = ({
    product,
    displayItemId,
    displayItemName,
    location,
  }: TrackClickHomeDisplayProductListProps) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeDisplayProduct, {
      product,
      isShownPriceCouponDiscounted: product?.hasApplicableProductCoupon,
      productPurchaseCouponDiscountPrice: product?.salePrice,
      displayItemId: displayItemId || product.id,
      displayItemName: displayItemName || product.name,
      location,
    })
  }

  const trackClickHomeSectionProductEventV4 = ({
    product,
    sectionName,
    sectionIndex,
  }: TrackClickHomeSectionProductV4Props) => {
    const properties = {
      productId: product.id,
      productName: product.name,
      productPrice: product.price,
      productPurchasePrice: product.salePrice,
      productDiscountRate: product.discountRate,
      sectionName,
      sectionIndex,
      brandId: product.brand.id,
      brandName: product.brand.name,
      supplyType: product.supplyType,
      isShownPriceCouponDiscounted: product?.hasApplicableProductCoupon,
      productPurchaseCouponDiscountPrice: product?.salePrice,
    }

    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeSectionProduct, properties)
  }

  const trackByDisplayCollectionType = ({
    product,
    displayCollectionId,
    displayCollectionType,
    title,
    location,
  }: TrackByDisplayCollectionTypeProps) => {
    match(displayCollectionType)
      .with(DisplayCollectionCollectionType.MagazineBackgroundImage, () => {
        trackClickHomeMagazineBackgroundImageProductEvent({ product, location })
      })
      .with(DisplayCollectionCollectionType.MagazineImage, () => {
        trackClickMagazineImageProductEvent({ product, location })
      })
      .with(DisplayCollectionCollectionType.TodaySpecialPrice, () => {
        trackClickHomeSpecialSaleProductEventV4({ product, location })
      })
      .with(DisplayCollectionCollectionType.NewArrivals, () => {
        trackClickHomeNewProductEventV4({ product, location })
      })
      .with(DisplayCollectionCollectionType.Normal, () => {
        trackClickHomeDisplayProductEventV4({
          product,
          displayItemId: displayCollectionId,
          displayItemName: title,
          location,
        })
        trackClickHomeSectionProductEventV4({ sectionName: title, product, location })
      })
  }

  return {
    trackClickHomeSectionProductEventV4,
    trackByDisplayCollectionType,
  }
}
